import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";

function parseResultFile(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) throw new Error("The selected result file is empty.");
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
      if (Array.isArray(parsed?.records)) return parsed.records;
      return [parsed];
    } catch (error) {
      if (!trimmed.includes("\n")) throw error;
    }
  }
  return trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      try {
        return JSON.parse(line);
      } catch (error) {
        throw new Error(`Invalid NDJSON on line ${index + 1}: ${error.message}`);
      }
    });
}

function inputValue(value) {
  return value == null ? "" : String(value);
}

function optionalNumber(value) {
  const trimmed = String(value ?? "").trim();
  return trimmed === "" ? null : Number(trimmed);
}

const COUNT_KEYS = ["pending", "exported", "applied", "needs_review", "failed"];

export default function ProductResearchAgentBridgeDialog({
  open,
  searchSessionId,
  onClose,
  onChanged,
  onOpenManual,
}) {
  const [state, setState] = useState({ counts: {}, exceptions: [] });
  const [outcomes, setOutcomes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(null);
  const fileInputRef = useRef(null);

  const loadState = useCallback(async () => {
    if (!searchSessionId) return;
    const response = await fetch(
      `/api/search-sessions/${searchSessionId}/product-research-agent/status`
    );
    const payload = await response.json();
    if (!response.ok || !payload?.ok) {
      throw new Error(payload?.error || "Failed to load Agent Product Research status");
    }
    setState({ counts: payload.counts || {}, exceptions: payload.exceptions || [] });
  }, [searchSessionId]);

  useEffect(() => {
    if (!open) return;
    setOutcomes([]);
    setMessage(null);
    setReviewDialog(null);
    loadState().catch((error) => setMessage({ severity: "error", text: error.message }));
  }, [open, loadState]);

  const prepareJobs = async (refresh = false) => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(
        `/api/search-sessions/${searchSessionId}/product-research-agent/jobs`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh }),
        }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || "Failed to prepare Product Research jobs");
      }
      setMessage({
        severity: "success",
        text: `Prepared ${payload.created?.length || 0} new job(s); ${payload.existing?.length || 0} existing job(s) retained; ${payload.skipped?.length || 0} researched card(s) skipped.`,
      });
      await loadState();
    } catch (error) {
      setMessage({ severity: "error", text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const downloadJobs = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(
        `/api/search-sessions/${searchSessionId}/product-research-agent/jobs/export`
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || "Failed to export Product Research jobs");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `product_research_jobs_session_${searchSessionId}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setMessage({ severity: "success", text: "Agent job file downloaded." });
      await loadState();
    } catch (error) {
      setMessage({ severity: "error", text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const records = parseResultFile(await file.text());
      const response = await fetch(
        `/api/search-sessions/${searchSessionId}/product-research-agent/results/import`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ records }),
        }
      );
      const payload = await response.json();
      if (!response.ok && !payload?.ok) {
        throw new Error(payload?.error || "Failed to import Product Research results");
      }
      setOutcomes(payload.outcomes || []);
      const summary = Object.entries(payload.counts || {})
        .map(([key, value]) => `${key}: ${value}`)
        .join(" · ");
      setMessage({
        severity: (payload.counts?.failed || payload.counts?.needs_review) ? "warning" : "success",
        text: `Imported ${records.length} record(s). ${summary}`,
      });
      await loadState();
      await onChanged?.();
    } catch (error) {
      setMessage({ severity: "error", text: error.message });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setBusy(false);
    }
  };

  const submitReview = async (item, action, overrides = {}, reason = "") => {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(
        `/api/search-sessions/${searchSessionId}/product-research-agent/results/${item.result_id}/review`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action, overrides, reason }),
        }
      );
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || "Failed to review Product Research result");
      }
      const verb = action === "reject" ? "Rejected" : "Applied";
      setMessage({
        severity: "success",
        text: `${verb} ${payload.card_name || item.card_name}.`,
      });
      setReviewDialog(null);
      await loadState();
      await onChanged?.();
    } catch (error) {
      setMessage({ severity: "error", text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const approveAndApply = async (item) => {
    if (!item.review_can_apply) return;
    const confirmed = window.confirm(
      `Apply the imported Product Research values for ${item.card_name}?`
    );
    if (!confirmed) return;
    await submitReview(item, "approve_apply");
  };

  const openEditReview = (item) => {
    setReviewDialog({
      mode: "edit",
      item,
      finalAverage: inputValue(item.final_average),
      finalLow: inputValue(item.final_low),
      finalHigh: inputValue(item.final_high),
      soldCount: inputValue(item.computed_sold_count),
      confirmedNoSales: Number(item.computed_sold_count) === 0,
      reason: "",
    });
  };

  const openRejectReview = (item) => {
    setReviewDialog({ mode: "reject", item, reason: "" });
  };

  const applyEditedReview = async () => {
    const dialog = reviewDialog;
    if (!dialog?.item || !dialog.reason.trim()) return;
    await submitReview(
      dialog.item,
      "edit_apply",
      {
        final_average: optionalNumber(dialog.finalAverage),
        final_low: optionalNumber(dialog.finalLow),
        final_high: optionalNumber(dialog.finalHigh),
        sold_count: optionalNumber(dialog.soldCount),
        confirmed_no_sales: dialog.confirmedNoSales,
      },
      dialog.reason.trim()
    );
  };

  const rejectReviewedResult = async () => {
    const dialog = reviewDialog;
    if (!dialog?.item || !dialog.reason.trim()) return;
    await submitReview(dialog.item, "reject", {}, dialog.reason.trim());
  };

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
        <DialogTitle>Agent Product Research — Session #{searchSessionId}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              The app prepares stable jobs and imports one JSON/NDJSON result batch. The browser agent works only in authenticated eBay Product Research; it does not need to operate this app between cards. Files contain no credentials.
            </Alert>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {COUNT_KEYS.map((key) => (
                <Chip key={key} label={`${key.replace("_", " ")}: ${state.counts?.[key] || 0}`} />
              ))}
              <Chip variant="outlined" label={`total: ${state.counts?.total || 0}`} />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
              <Button variant="contained" onClick={() => prepareJobs(false)} disabled={busy}>
                Prepare Pending Jobs
              </Button>
              <Button variant="outlined" onClick={() => prepareJobs(true)} disabled={busy}>
                Refresh All Jobs
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadJobs} disabled={busy || !(state.counts?.pending || state.counts?.exported)}>
                Download Job JSON
              </Button>
              <Button variant="outlined" startIcon={<UploadFileIcon />} component="label" disabled={busy}>
                Import Result File
                <input
                  ref={fileInputRef}
                  hidden
                  type="file"
                  accept=".json,.ndjson,application/json,application/x-ndjson,text/plain"
                  onChange={(event) => importFile(event.target.files?.[0])}
                />
              </Button>
              <Button
                variant="text"
                startIcon={<RefreshIcon />}
                onClick={() => loadState().catch((error) => setMessage({ severity: "error", text: error.message }))}
                disabled={busy}
              >
                Refresh Status
              </Button>
            </Stack>

            {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}

            {outcomes.length ? (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Latest import outcomes</Typography>
                <List dense disablePadding>
                  {outcomes.map((item, index) => (
                    <ListItem key={`${item.job_key || "record"}-${index}`} divider>
                      <ListItemText
                        primary={`${item.card_name || item.job_key || `Record ${index + 1}`} — ${item.outcome || "unknown"}`}
                        secondary={(item.errors || [item.error]).filter(Boolean).join("; ") || (item.summary_id ? `Summary #${item.summary_id}` : "")}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : null}

            <Divider />

            <Box>
              <Typography variant="subtitle1">Needs review</Typography>
              {!state.exceptions?.length ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No unresolved agent results.
                </Typography>
              ) : (
                <List dense>
                  {state.exceptions.map((item) => (
                    <ListItem key={item.result_id} divider alignItems="flex-start" sx={{ gap: 2 }}>
                      <ListItemText
                        sx={{ minWidth: 0 }}
                        primary={`${item.card_name} — ${item.status}`}
                        secondary={
                          <Stack spacing={0.25} component="span">
                            <Typography variant="caption" component="span">
                              Raw avg: {item.raw_average ?? "—"} · Final avg: {item.final_average ?? "—"} · Sold: {item.computed_sold_count ?? "—"} · Confidence: {item.confidence || "—"}
                            </Typography>
                            {(item.minimum_price_filter != null || item.maximum_price_filter != null || item.adjustment_reason) ? (
                              <Typography variant="caption" component="span">
                                Filter: {item.minimum_price_filter ?? "—"} to {item.maximum_price_filter ?? "—"} · {item.adjustment_reason || "No reason recorded"}
                              </Typography>
                            ) : null}
                            {(item.validation_errors || []).length ? (
                              <Typography variant="caption" color={item.review_can_apply ? "warning.main" : "error"} component="span">
                                {(item.validation_errors || []).join("; ")}
                              </Typography>
                            ) : null}
                            {item.review_can_apply ? (
                              <Typography variant="caption" color="success.main" component="span">
                                Values are structurally valid and can be applied after human approval.
                              </Typography>
                            ) : (item.review_blocking_errors || []).length ? (
                              <Typography variant="caption" color="error" component="span">
                                Manual or new research is required before application.
                              </Typography>
                            ) : null}
                          </Stack>
                        }
                      />
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ minWidth: { md: 430 } }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => approveAndApply(item)}
                          disabled={busy || !item.review_can_apply}
                          title={item.review_can_apply ? "Apply the imported values" : "Blocking validation errors must be resolved first"}
                        >
                          Approve &amp; Apply
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEditReview(item)}
                          disabled={busy || !item.review_can_apply}
                          title={item.review_can_apply ? "Adjust the aggregate values, then apply" : "Blocking validation errors must be resolved first"}
                        >
                          Edit &amp; Apply
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => openRejectReview(item)}
                          disabled={busy}
                        >
                          Reject Result
                        </Button>
                        <Button size="small" onClick={() => onOpenManual?.(item.card_search_input_id)} disabled={busy}>
                          Enter Research Data
                        </Button>
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reviewDialog?.mode === "edit"}
        onClose={busy ? undefined : () => setReviewDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit &amp; Apply — {reviewDialog?.item?.card_name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="info">
              The imported evidence remains in the audit record. These reviewed aggregate values will drive the saved summary and Suggested Buy.
            </Alert>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Final average"
                type="number"
                value={reviewDialog?.finalAverage ?? ""}
                onChange={(event) => setReviewDialog((current) => ({ ...current, finalAverage: event.target.value }))}
                inputProps={{ min: 0, step: "0.01" }}
                fullWidth
              />
              <TextField
                label="Sold 30d"
                type="number"
                value={reviewDialog?.soldCount ?? ""}
                onChange={(event) => setReviewDialog((current) => ({ ...current, soldCount: event.target.value }))}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Final low"
                type="number"
                value={reviewDialog?.finalLow ?? ""}
                onChange={(event) => setReviewDialog((current) => ({ ...current, finalLow: event.target.value }))}
                inputProps={{ min: 0, step: "0.01" }}
                fullWidth
              />
              <TextField
                label="Final high"
                type="number"
                value={reviewDialog?.finalHigh ?? ""}
                onChange={(event) => setReviewDialog((current) => ({ ...current, finalHigh: event.target.value }))}
                inputProps={{ min: 0, step: "0.01" }}
                fullWidth
              />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(reviewDialog?.confirmedNoSales)}
                  onChange={(event) => setReviewDialog((current) => ({ ...current, confirmedNoSales: event.target.checked }))}
                />
              }
              label="Confirm genuine zero sales"
            />
            <TextField
              label="Review reason"
              value={reviewDialog?.reason ?? ""}
              onChange={(event) => setReviewDialog((current) => ({ ...current, reason: event.target.value }))}
              required
              multiline
              minRows={2}
              helperText="Record why the imported values were adjusted or accepted with changes."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyEditedReview}
            disabled={busy || !reviewDialog?.reason?.trim()}
          >
            Apply Reviewed Values
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reviewDialog?.mode === "reject"}
        onClose={busy ? undefined : () => setReviewDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject Result — {reviewDialog?.item?.card_name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="warning">
              This imported result will not be applied. The job will return to pending so it can be exported and researched again.
            </Alert>
            <TextField
              label="Rejection reason"
              value={reviewDialog?.reason ?? ""}
              onChange={(event) => setReviewDialog((current) => ({ ...current, reason: event.target.value }))}
              required
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(null)} disabled={busy}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={rejectReviewedResult}
            disabled={busy || !reviewDialog?.reason?.trim()}
          >
            Reject Result
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
